/**
 * useTopicVoiceDesign (Sprint 16) — resolve + edit tone-of-voice and
 * design-system for a single topic, with 3-tier precedence:
 *
 *   topic.toneOfVoiceOverride → strategy.toneOfVoice → project.toneOfVoice
 *   topic.designSystemOverride → strategy.designSystem → project.designSystem
 *     (falls back to project.designDescription — legacy freeform)
 *
 * Mirrors the backend resolver (voiceDesignResolver.ts) so the UI shows
 * exactly what the post generator will consume. Writes go to
 * `topic_backlog/{topicId}.{toneOfVoiceOverride|designSystemOverride}` via
 * setDoc merge; Firestore listeners then flow back into the returned state.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot, setDoc, serverTimestamp, deleteField } from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';

type Source = 'topic_override' | 'strategy' | 'project' | 'none';

function isPresent(v: unknown): boolean {
  if (v == null) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (typeof v === 'object' && !Array.isArray(v)) {
    return Object.keys(v as Record<string, unknown>).length > 0;
  }
  return true;
}

interface TopicDoc {
  toneOfVoiceOverride?: unknown;
  designSystemOverride?: unknown;
}

interface StrategyDoc {
  toneOfVoice?: unknown;
  designSystem?: unknown;
}

interface ProjectDoc {
  toneOfVoice?: unknown;
  designSystem?: unknown;
  designDescription?: unknown;
}

export interface ResolvedVoiceDesign {
  tone: { value: unknown; source: Source };
  design: { value: unknown; source: Source };
  /** What strategy/project currently offer (used to prefill override editor). */
  inheritedTone: { value: unknown; source: Source };
  inheritedDesign: { value: unknown; source: Source };
  /** True while Firestore subscriptions haven't yet delivered first snapshots. */
  loading: boolean;
  saveToneOverride: (value: unknown) => Promise<void>;
  saveDesignOverride: (value: unknown) => Promise<void>;
  clearToneOverride: () => Promise<void>;
  clearDesignOverride: () => Promise<void>;
}

export function useTopicVoiceDesign(
  projectId: string | null,
  topicId: string | null,
  strategyId: string | null | undefined,
): ResolvedVoiceDesign {
  const [topic, setTopic] = useState<TopicDoc | null>(null);
  const [strategy, setStrategy] = useState<StrategyDoc | null>(null);
  const [project, setProject] = useState<ProjectDoc | null>(null);
  const [topicLoaded, setTopicLoaded] = useState(false);
  const [projectLoaded, setProjectLoaded] = useState(false);
  const [strategyLoaded, setStrategyLoaded] = useState(false);

  const db = useMemo(() => CoreFirebaseService.getInstance().firestore, []);

  // Subscribe — topic.
  useEffect(() => {
    if (!projectId || !topicId) { setTopicLoaded(true); return; }
    const ref = doc(db, 'projects', projectId, 'topic_backlog', topicId);
    const unsub = onSnapshot(ref, (snap) => {
      setTopic((snap.data() as TopicDoc) ?? {});
      setTopicLoaded(true);
    }, () => setTopicLoaded(true));
    return () => unsub();
  }, [db, projectId, topicId]);

  // Subscribe — project.
  useEffect(() => {
    if (!projectId) { setProjectLoaded(true); return; }
    const ref = doc(db, 'projects', projectId);
    const unsub = onSnapshot(ref, (snap) => {
      setProject((snap.data() as ProjectDoc) ?? {});
      setProjectLoaded(true);
    }, () => setProjectLoaded(true));
    return () => unsub();
  }, [db, projectId]);

  // Subscribe — strategy (optional).
  useEffect(() => {
    if (!projectId || !strategyId) { setStrategyLoaded(true); setStrategy(null); return; }
    const ref = doc(db, 'projects', projectId, 'content_strategies', strategyId);
    const unsub = onSnapshot(ref, (snap) => {
      setStrategy((snap.data() as StrategyDoc) ?? {});
      setStrategyLoaded(true);
    }, () => setStrategyLoaded(true));
    return () => unsub();
  }, [db, projectId, strategyId]);

  // Resolve both — topic override wins, then strategy, then project.
  // `inheritedTone/Design` is what's available WITHOUT the topic override
  // (used to prefill the edit form when the user clicks "Customize for this topic").
  const { tone, design, inheritedTone, inheritedDesign } = useMemo(() => {
    const resolveTone = (): { value: unknown; source: Source } => {
      if (isPresent(topic?.toneOfVoiceOverride)) return { value: topic!.toneOfVoiceOverride, source: 'topic_override' };
      if (isPresent(strategy?.toneOfVoice)) return { value: strategy!.toneOfVoice, source: 'strategy' };
      if (isPresent(project?.toneOfVoice)) return { value: project!.toneOfVoice, source: 'project' };
      return { value: undefined, source: 'none' };
    };
    const resolveDesign = (): { value: unknown; source: Source } => {
      if (isPresent(topic?.designSystemOverride)) return { value: topic!.designSystemOverride, source: 'topic_override' };
      if (isPresent(strategy?.designSystem)) return { value: strategy!.designSystem, source: 'strategy' };
      if (isPresent(project?.designSystem)) return { value: project!.designSystem, source: 'project' };
      if (isPresent(project?.designDescription)) return { value: project!.designDescription, source: 'project' };
      return { value: undefined, source: 'none' };
    };
    const resolveInheritedTone = (): { value: unknown; source: Source } => {
      if (isPresent(strategy?.toneOfVoice)) return { value: strategy!.toneOfVoice, source: 'strategy' };
      if (isPresent(project?.toneOfVoice)) return { value: project!.toneOfVoice, source: 'project' };
      return { value: undefined, source: 'none' };
    };
    const resolveInheritedDesign = (): { value: unknown; source: Source } => {
      if (isPresent(strategy?.designSystem)) return { value: strategy!.designSystem, source: 'strategy' };
      if (isPresent(project?.designSystem)) return { value: project!.designSystem, source: 'project' };
      if (isPresent(project?.designDescription)) return { value: project!.designDescription, source: 'project' };
      return { value: undefined, source: 'none' };
    };
    return {
      tone: resolveTone(),
      design: resolveDesign(),
      inheritedTone: resolveInheritedTone(),
      inheritedDesign: resolveInheritedDesign(),
    };
  }, [topic, strategy, project]);

  // Mutations — write topic-level override through setDoc(..., { merge: true })
  // so unrelated topic fields are preserved. Clear operations use deleteField
  // so the override returns to null and the resolver falls back through.
  const topicRef = useCallback(() => {
    if (!projectId || !topicId) throw new Error('projectId + topicId required to write');
    return doc(db, 'projects', projectId, 'topic_backlog', topicId);
  }, [db, projectId, topicId]);

  const saveToneOverride = useCallback(async (value: unknown) => {
    await setDoc(topicRef(), {
      toneOfVoiceOverride: value,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  }, [topicRef]);

  const saveDesignOverride = useCallback(async (value: unknown) => {
    await setDoc(topicRef(), {
      designSystemOverride: value,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  }, [topicRef]);

  const clearToneOverride = useCallback(async () => {
    await setDoc(topicRef(), {
      toneOfVoiceOverride: deleteField(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  }, [topicRef]);

  const clearDesignOverride = useCallback(async () => {
    await setDoc(topicRef(), {
      designSystemOverride: deleteField(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  }, [topicRef]);

  return {
    tone,
    design,
    inheritedTone,
    inheritedDesign,
    loading: !(topicLoaded && projectLoaded && strategyLoaded),
    saveToneOverride,
    saveDesignOverride,
    clearToneOverride,
    clearDesignOverride,
  };
}
